import { useCallback, useEffect, useRef, useState } from 'react';
import useSessionStorage from '@/hooks/useSessionStorage';

interface DragPosition {
  bottom: number;
  right: number;
  left: number;
}

interface UseAIButtonDragReturn {
  position: DragPosition;
  isDragging: boolean;
  consumeDragEndClick: () => boolean;
  dragHandlers: {
    onPointerDown: (event: React.PointerEvent) => void;
  };
}

const STORAGE_KEY = 'ai-button-position';
const DEFAULT_POSITION: DragPosition = { bottom: 32, right: 32, left: 0 };
const DRAG_THRESHOLD = 5;
const DRAG_THRESHOLD_SQ = DRAG_THRESHOLD * DRAG_THRESHOLD;
const FALLBACK_SIZE = 56;

const constrainToViewport = (dragPosition: DragPosition, buttonWidth: number, buttonHeight: number): DragPosition => {
  const viewportWidth = window.innerWidth;
  const maxRight = viewportWidth - buttonWidth;
  const maxBottom = window.innerHeight - buttonHeight;

  const constrainedRight = Math.max(0, Math.min(maxRight, dragPosition.right));
  const constrainedBottom = Math.max(0, Math.min(maxBottom, dragPosition.bottom));
  const left = viewportWidth - constrainedRight - buttonWidth;

  return { right: constrainedRight, bottom: constrainedBottom, left };
};

const useAIButtonDrag = (elementRef: React.RefObject<HTMLElement | null>): UseAIButtonDragReturn => {
  const { value: storedPosition, setValue: setStoredPosition } = useSessionStorage<DragPosition>(
    STORAGE_KEY,
    DEFAULT_POSITION,
  );

  const [position, setPosition] = useState<DragPosition>(() => {
    if (storedPosition.left === 0 && storedPosition.right > 0) {
      const computedLeft = window.innerWidth - storedPosition.right - FALLBACK_SIZE;
      return { ...storedPosition, left: Math.max(0, computedLeft) };
    }
    return storedPosition;
  });
  const [isDragging, setIsDragging] = useState(false);
  const suppressClickRef = useRef(false);

  const positionRef = useRef<DragPosition>(position);
  const setStoredPositionRef = useRef(setStoredPosition);
  const isMountedRef = useRef(true);

  useEffect(() => {
    positionRef.current = position;
  }, [position]);

  useEffect(() => {
    setStoredPositionRef.current = setStoredPosition;
  }, [setStoredPosition]);

  const dragState = useRef({
    startX: 0,
    startY: 0,
    startRight: 0,
    startBottom: 0,
    hasMoved: false,
    buttonWidth: FALLBACK_SIZE,
    buttonHeight: FALLBACK_SIZE,
  });
  const animationFrameRef = useRef<number | undefined>(undefined);
  const moveListenerRef = useRef<((e: PointerEvent) => void) | null>(null);
  const upListenerRef = useRef<(() => void) | null>(null);

  const handlePointerDown = useCallback(
    (event: React.PointerEvent) => {
      event.preventDefault();

      const currentPosition = positionRef.current;
      const el = elementRef.current;
      const rect = el ? el.getBoundingClientRect() : null;
      const buttonWidth = rect ? rect.width : FALLBACK_SIZE;
      const buttonHeight = rect ? rect.height : FALLBACK_SIZE;
      dragState.current = {
        startX: event.clientX,
        startY: event.clientY,
        startRight: currentPosition.right,
        startBottom: currentPosition.bottom,
        hasMoved: false,
        buttonWidth,
        buttonHeight,
      };

      const handlePointerMove = (moveEvent: PointerEvent) => {
        const { startX, startY, startRight, startBottom } = dragState.current;

        const deltaX = startX - moveEvent.clientX;
        const deltaY = startY - moveEvent.clientY;
        const distanceSq = deltaX * deltaX + deltaY * deltaY;
        if (!dragState.current.hasMoved && distanceSq < DRAG_THRESHOLD_SQ) return;

        if (!dragState.current.hasMoved) {
          dragState.current.hasMoved = true;
          if (isMountedRef.current) setIsDragging(true);
        }

        if (animationFrameRef.current) return;

        animationFrameRef.current = requestAnimationFrame(() => {
          const newPosition = constrainToViewport(
            { right: startRight + deltaX, bottom: startBottom + deltaY, left: 0 },
            dragState.current.buttonWidth,
            dragState.current.buttonHeight,
          );
          positionRef.current = newPosition;
          const dragEl = elementRef.current;
          if (dragEl) {
            const useLeftAnchor = newPosition.left < newPosition.right;
            dragEl.style.bottom = `${newPosition.bottom}px`;
            if (useLeftAnchor) {
              dragEl.style.left = `${newPosition.left}px`;
              dragEl.style.right = 'auto';
            } else {
              dragEl.style.right = `${newPosition.right}px`;
              dragEl.style.left = 'auto';
            }
          }
          animationFrameRef.current = undefined;
        });
      };

      const handlePointerUp = () => {
        window.removeEventListener('pointermove', handlePointerMove);
        window.removeEventListener('pointerup', handlePointerUp);
        moveListenerRef.current = null;
        upListenerRef.current = null;

        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = undefined;
        }

        if (dragState.current.hasMoved) {
          suppressClickRef.current = true;
          if (isMountedRef.current) setPosition(positionRef.current);
          setStoredPositionRef.current(positionRef.current);
        }

        dragState.current = {
          startX: 0,
          startY: 0,
          startRight: 0,
          startBottom: 0,
          hasMoved: false,
          buttonWidth: FALLBACK_SIZE,
          buttonHeight: FALLBACK_SIZE,
        };
        if (isMountedRef.current) setIsDragging(false);
      };

      moveListenerRef.current = handlePointerMove;
      upListenerRef.current = handlePointerUp;
      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
    },
    [elementRef],
  );

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (moveListenerRef.current) window.removeEventListener('pointermove', moveListenerRef.current);
      if (upListenerRef.current) window.removeEventListener('pointerup', upListenerRef.current);
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
  }, []);

  return {
    position,
    isDragging,
    consumeDragEndClick: () => {
      if (!suppressClickRef.current) {
        return false;
      }

      suppressClickRef.current = false;
      return true;
    },
    dragHandlers: { onPointerDown: handlePointerDown },
  };
};

export default useAIButtonDrag;
